import { useEffect, useState } from "react";
import { Users, Save, RefreshCw, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { nb } from "@/lib/format";

export function GfRegister({ meetingId, m, gf, onChange }) {
  const register = m.register || [];
  const isChair = !!gf?.is_chair;
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    setRows(register.map((e) => ({ id: e.id, present: e.present !== false, proxy_to: e.proxy_to || "" })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.id, JSON.stringify(register.map((e) => [e.id, e.present, e.proxy_to]))]);

  const build = async (refresh) => {
    setBuilding(true);
    try {
      await api.post(`/meetings/${meetingId}/gf/register${refresh ? "?refresh=true" : ""}`);
      toast.success(refresh ? "Register oppdatert fra aksjeeierboka" : "Aksjonærregister bygget");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBuilding(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/meetings/${meetingId}/gf/register`, {
        entries: rows.map((r) => ({ id: r.id, present: r.present, proxy_to: r.proxy_to || null })),
      });
      toast.success("Oppmøte og fullmakter lagret");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  if (register.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="gf-register-empty">
        <Users className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Aksjonærregisteret er ikke bygget ennå.</p>
        {isChair ? (
          <Button className="mt-4 gap-2" onClick={() => build(false)} disabled={building} data-testid="gf-build-register-btn">
            {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Bygg aksjonærregister
          </Button>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">Møteleder må bygge registeret.</p>
        )}
      </div>
    );
  }

  const totalVotes = gf?.total_votes || 0;
  const represented = gf?.represented_votes || 0;
  const pct = gf?.represented_pct || 0;

  return (
    <div className="space-y-5">
      <div data-testid="gf-quorum" className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-primary/25 bg-primary/[0.04] p-5">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-lime text-lime-foreground"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <p className="font-headings text-lg font-extrabold tracking-tight">{pct}% representert</p>
            <p className="text-xs text-muted-foreground">{nb(represented)} av {nb(totalVotes)} stemmer møter (egne + fullmakter)</p>
          </div>
        </div>
        {isChair ? (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => build(true)} disabled={building} data-testid="gf-refresh-register-btn">
            {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Oppdater fra aksjeeierbok
          </Button>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-border/60">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Aksjonær</th>
              <th className="px-4 py-3 text-right font-medium">Aksjer</th>
              <th className="px-4 py-3 text-right font-medium">Stemmer</th>
              <th className="px-4 py-3 text-center font-medium">Frammøtt</th>
              <th className="px-4 py-3 text-left font-medium">Fullmakt til</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {register.map((e) => {
              const row = rows.find((r) => r.id === e.id) || { present: true, proxy_to: "" };
              return (
                <tr key={e.id} data-testid={`gf-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{e.name}</p>
                    <p className="text-xs text-muted-foreground">{e.holder_type === "selskap" ? "Selskap" : "Person"}{e.id_number ? ` · ${e.id_number}` : ""}</p>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{nb(e.shares)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{nb(e.votes)}</td>
                  <td className="px-4 py-3 text-center">
                    {row.proxy_to ? (
                      <span className="text-[11px] text-muted-foreground" data-testid={`gf-represented-${e.id}`}>
                        Repr. av {(register.find((x) => x.id === row.proxy_to)?.name || "fullmektig").split(" ")[0]}
                      </span>
                    ) : (
                      <Checkbox
                        data-testid={`gf-present-${e.id}`}
                        checked={row.present}
                        disabled={!isChair}
                        onCheckedChange={(v) => setRow(e.id, { present: !!v })}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={row.proxy_to || "none"}
                      disabled={!isChair}
                      onValueChange={(v) => setRow(e.id, v === "none" ? { proxy_to: "", present: true } : { proxy_to: v, present: false })}
                    >
                      <SelectTrigger data-testid={`gf-proxy-${e.id}`} className="h-9 w-full max-w-[220px]"><SelectValue placeholder="Ingen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ingen (stemmer selv)</SelectItem>
                        {register.filter((o) => o.id !== e.id).map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isChair ? (
        <div className="flex justify-end">
          <Button className="gap-2" onClick={save} disabled={saving} data-testid="gf-save-register-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre oppmøte &amp; fullmakter
          </Button>
        </div>
      ) : null}
    </div>
  );
}
